import { useState, useEffect, useCallback } from 'react';
import { prequalificationService } from '../services/api';
import { toLocalDateString } from '../utils/helpers';

export function usePrequalifications({ enabled = true } = {}) {
    const [prequalifications, setLeads] = useState([]);
    const [loading, setLoading] = useState(enabled);
    const [error, setError] = useState(null);
    const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, totalPages: 1 });
    const [hasMore, setHasMore] = useState(false);

    // Transform a raw prequalification from API to UI shape
    const transformLead = (prequalification) => {
        const profileObj = prequalification.profile || prequalification.contact;
        const clientNameVal = profileObj?.clientName || profileObj?.contactPersonName || (profileObj ? [profileObj.firstName, profileObj.lastName].filter(Boolean).join(' ') : null) || prequalification.title || 'Client #' + prequalification.id;

        return {
            ...prequalification,
            clientName: clientNameVal,
            projectName: profileObj?.projectName || prequalification.title || '',
            stage: prequalification.status === 'New Prequalification' ? 'New' : prequalification.status,
            amount: prequalification.proposalValue || prequalification.finalAmount || prequalification.quotationAmount || prequalification.clientBudget || 0,
            inquiryDate: toLocalDateString(prequalification.createdAt),
            manager: prequalification.assignee ? prequalification.assignee.username : 'Unassigned',
            phone: profileObj?.mobile || profileObj?.phone || '',
            source: prequalification.source?.name || '',
            meta: (prequalification.campaignName || prequalification.adsetName || prequalification.adName) ? {
                campaign_name: prequalification.campaignName || null,
                adset_name: prequalification.adsetName || null,
                ad_name: prequalification.adName || null,
            } : null
        };
    };

    /**
     * fetchLeads — fetches paginated prequalifications from server with optional filters.
     * All filtering (search, status, assignedTo, date) is done server-side.
     * @param {object} params - { status, search, assignedTo, startDate, endDate, page, limit }
     * @param {boolean} append - if true, appends to existing list (load more)
     */
    const fetchLeads = useCallback(async (params = {}, append = false) => {
        setLoading(true);
        try {
            // Default to all prequalifications
            const fetchParams = { limit: 'all', ...params };
            // Remove empty/All values to keep URL clean
            Object.keys(fetchParams).forEach(k => {
                if (fetchParams[k] === '' || fetchParams[k] === 'All' || fetchParams[k] === null || fetchParams[k] === undefined) {
                    delete fetchParams[k];
                }
            });

            const response = await prequalificationService.getAll(fetchParams);
            const transformedLeads = response.data.map(transformLead);

            if (append) {
                setLeads(prev => [...prev, ...transformedLeads]);
            } else {
                setLeads(transformedLeads);
            }
            setPagination(response.pagination);
            setHasMore(response.pagination?.page < response.pagination?.totalPages);
            setError(null);
        } catch (err) {
            console.error('Error fetching prequalifications:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    const loadMoreLeads = useCallback(async (params = {}) => {
        if (hasMore && !loading && pagination) {
            await fetchLeads({ ...params, page: pagination.page + 1 }, true);
        }
    }, [hasMore, loading, pagination, fetchLeads]);

    const [stats, setStats] = useState({ revenue: 0, pipe: 0, total: 0, conversion: 0, wonCount: 0 });
    const [analytics, setAnalytics] = useState(null);
    const [analyticsLoading, setAnalyticsLoading] = useState(false);

    const fetchStats = useCallback(async () => {
        try {
            const data = await prequalificationService.getStats();
            setStats(data);
        } catch (err) {
            console.error('Error fetching prequalifications stats:', err);
        }
    }, []);

    /**
     * fetchAnalytics — fetches aggregated dashboard data from /api/prequalifications/analytics.
     * Returns KPIs, charts, stale prequalifications — all computed server-side. Scales to millions.
     * @param {object} params - { dateRange: 'week'|'month'|'quarter'|'year'|'all' }
     */
    const fetchAnalytics = useCallback(async (params = {}) => {
        setAnalyticsLoading(true);
        try {
            const data = await prequalificationService.getAnalytics(params);
            setAnalytics(data);
            return data;
        } catch (err) {
            console.error('Error fetching prequalifications analytics:', err);
            return null;
        } finally {
            setAnalyticsLoading(false);
        }
    }, []);

    // Initial fetch
    useEffect(() => {
        if (enabled) {
            fetchLeads();
            fetchStats();
        } else {
            setLoading(false);
        }
    }, [fetchLeads, fetchStats, enabled]);

    const addLead = async (leadData) => {
        try {
            const newLead = await prequalificationService.create(leadData);
            const transformedLead = transformLead(newLead);
            setLeads(prev => [transformedLead, ...prev]);
            return newLead;
        } catch (err) {
            throw err;
        }
    };

    const updateLead = async (id, leadData) => {
        try {
            const updatedLead = await prequalificationService.update(id, leadData);
            const transformedLead = transformLead(updatedLead);
            setLeads(prev => prev.map(prequalification => prequalification.id === id ? { ...prequalification, ...transformedLead } : prequalification));
            return updatedLead;
        } catch (err) {
            throw err;
        }
    };

    const deleteLead = async (id) => {
        try {
            await prequalificationService.delete(id);
            setLeads(prev => prev.filter(prequalification => prequalification.id !== id));
        } catch (err) {
            throw err;
        }
    };

    const deleteAllLeads = async () => {
        try {
            await prequalificationService.deleteAll();
            await fetchLeads();
        } catch (err) {
            throw err;
        }
    };

    const addLeadNote = async (id, note, type = 'NOTE') => {
        try {
            await prequalificationService.addNote(id, { content: note, type });
        } catch (err) {
            throw err;
        }
    };

    // Optimistic local-only patch — no API call, instant UI feedback
    const patchLead = (id, patch) => {
        setLeads(prev => prev.map(prequalification =>
            prequalification.id === id ? { ...prequalification, ...patch } : prequalification
        ));
    };

    return {
        prequalifications,
        loading,
        error,
        pagination,
        hasMore,
        fetchLeads,
        loadMoreLeads,
        addLead,
        updateLead,
        patchLead,
        deleteLead,
        deleteAllLeads,
        addLeadNote,
        stats,
        // Analytics (for dashboard)
        analytics,
        analyticsLoading,
        fetchAnalytics,
    };
}
